import { klona } from 'klona'
import type { Widget } from '@dashboard/types/config'

export const WIDGET_TYPES = ['modal', 'takeover', 'toaster', 'bar', 'inline'] as const
export type WidgetType = (typeof WIDGET_TYPES)[number]

export const TYPE_SCALE_PRESETS: Record<
  's' | 'm' | 'l',
  {
    headline: string
    description: string
    answers: string
    formFields: string
    submitButton: string
    headerTitle: string
    headerSubtitle: string
  }
> = {
  s: {
    headline: '20px',
    description: '14px',
    answers: '12px',
    formFields: '12px',
    submitButton: '12px',
    headerTitle: '14px',
    headerSubtitle: '12px'
  },
  m: {
    headline: '24px',
    description: '16px',
    answers: '13px',
    formFields: '13px',
    submitButton: '13px',
    headerTitle: '16px',
    headerSubtitle: '13px'
  },
  l: {
    headline: '28px',
    description: '18px',
    answers: '16px',
    formFields: '16px',
    submitButton: '16px',
    headerTitle: '18px',
    headerSubtitle: '15px'
  }
}

export const WIDGET_THEMES = [
  { id: 'plain', name: 'Plain', description: 'Will not show images. Center aligned.' },
  { id: 'baloo', name: 'Baloo', description: 'Supports images. Hero area with image and header copy.' },
  { id: 'paddington', name: 'Paddington', description: 'Supports images. Hero area with image.' },
  { id: 'petey', name: 'Petey', description: 'Supports images. Image on left constrained to bounding area.' },
  { id: 'yogi', name: 'Yogi', description: 'Supports images. Image on left centered within bounding area.' },
  {
    id: 'unstyled',
    name: 'Unstyled',
    description: "No RightMessage styling. Your site's CSS styles it using documented classes."
  }
] as const
export type WidgetTheme = (typeof WIDGET_THEMES)[number]
export const IMAGE_THEMES: readonly string[] = ['baloo', 'paddington', 'petey', 'yogi']

export function widgetTheme(id: string | null | undefined): WidgetTheme {
  return WIDGET_THEMES.find((theme) => theme.id === id) ?? WIDGET_THEMES[0]
}

export function allowedThemes(type: WidgetType): string[] {
  return type === 'toaster'
    ? ['plain', 'paddington', 'unstyled']
    : ['plain', 'baloo', 'paddington', 'petey', 'yogi', 'unstyled']
}

// Everything the Design tab edits except the theme, which callers pick per widget type.
const DESIGN_KEYS = [
  'palette',
  'paletteDark',
  'darkMode',
  'font',
  'headingFont',
  'typeScale',
  'fontSize',
  'structure',
  'look',
  'overrides',
  'customCss',
  'style',
  'headerInfo',
  'progressBar',
  'buttonAnimationTrigger'
] as const satisfies readonly (keyof Widget['options'])[]

export function withCopiedDesign(target: Widget['options'], source: Widget['options']): Widget['options'] {
  const options = { ...target }
  for (const key of DESIGN_KEYS) {
    Object.assign(options, { [key]: klona(source[key]) })
  }
  return options
}

export function defaultWidgetOptions(type: WidgetType): Widget['options'] {
  return {
    theme: 'plain',
    palette: {
      borderColor: '#5d6b91',
      contrastColor: '#3182CE',
      backgroundColor: '#ffffff',
      textOnContrastColor: '#EBF8FF',
      textOnBackgroundColor: '#1A202C',
      answerButtonColor: null,
      textOnAnswerButtonColor: null,
      formFieldColor: null,
      formFieldTextColor: null,
      formFieldBorderColor: null,
      placeholderColor: null,
      linkColor: null,
      backdropColor: null,
      backgroundGradientFrom: null,
      backgroundGradientTo: null,
      backdropOpacity: null,
      questionHeadingColor: null,
      questionTextColor: null,
      offerHeadingColor: null,
      offerTextColor: null,
      headerTitleColor: null,
      headerSubtitleColor: null
    },
    paletteDark: null,
    darkMode: 'off',
    headingFont: null,
    typeScale: 'm',
    fontSize: {
      lineHeight: 1.5,
      headlineFontSize: type === 'bar' ? '17px' : TYPE_SCALE_PRESETS.m.headline,
      formFieldFontSize: type === 'bar' ? '15px' : TYPE_SCALE_PRESETS.m.formFields,
      descriptionFontSize: TYPE_SCALE_PRESETS.m.description,
      answerButtonFontSize: type === 'toaster' ? '14px' : TYPE_SCALE_PRESETS.m.answers,
      submitButtonFontSize: type === 'bar' ? '16px' : type === 'toaster' ? '15px' : TYPE_SCALE_PRESETS.m.submitButton,
      headingFontWeight: 700,
      bodyFontWeight: 400,
      buttonFontWeight: null,
      submitButtonFontWeight: null,
      headerTitleFontSize: TYPE_SCALE_PRESETS.m.headerTitle,
      headerSubtitleFontSize: TYPE_SCALE_PRESETS.m.headerSubtitle
    },
    customCss: '',
    placement: type === 'bar' ? 'top' : type === 'toaster' ? 'right' : null,
    structure: {
      formFieldSpacing: '8px',
      buttonBorderRadius: type === 'toaster' ? '4px' : '0px',
      submitButtonBorderRadius: null,
      answerButtonSpacing: type === 'toaster' ? '11px' : '8px',
      containerBorderWidth: type === 'bar' ? '2px' : '0px',
      containerBorderRadius: '8px',
      widgetWidth: null,
      fieldBorderRadius: null,
      buttonLetterSpacing: null,
      submitButtonLetterSpacing: null,
      imageHeight: null,
      backdropBlur: null,
      containerPaddingY: null,
      containerPaddingX: null,
      titleSpacing: null,
      contentSpacing: null,
      submitSpacing: null,
      headerSpacing: null
    },
    headerInfo: { isEnabled: false, style: 'left', imageUrl: '', title: '', subtitle: '' },
    progressBar: { isEnabled: false, baseColor: '#60a5fa' },
    style: { shadowSize: 'lg' },
    displayTargets: { phones: true, desktop: true, tablets: true },
    backButtonLabel: '← Go back',
    backButtonEnabled: false,
    nextButtonLabel: 'Next',
    skipButtonLabel: 'Skip',
    buttonAnimationTrigger: '',
    look: {
      density: 'comfortable',
      buttonStyle: 'solid',
      buttonSize: 'm',
      buttonFullWidth: null,
      buttonUppercase: false,
      buttonHover: null,
      submitButtonStyle: null,
      submitButtonSize: null,
      submitButtonFullWidth: null,
      submitButtonUppercase: null,
      submitButtonHover: null,
      fieldStyle: 'boxed',
      labelPosition: null,
      borderStyle: 'solid',
      backgroundType: 'color',
      backgroundGradientAngle: null,
      backgroundImageUrl: null,
      imagePosition: null,
      entrance: null,
      entranceSpeed: 'normal',
      stepTransition: null,
      stepTransitionFade: null,
      closeButtonStyle: null,
      closeButtonPosition: null,
      backButtonStyle: null,
      skipButtonStyle: null
    }
  }
}
